import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CTAButton, Icon, Avatar } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';

/**
 * Module 4 — Action Plan Modal
 *
 * Inserts into `action_log` (Module 6).
 * source_activity = 'creator_alert' (default for this entry point).
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - onSubmit: (payload) => Promise — caller does the supabase insert
 *   - creator: the creator row (for prefill)
 *   - contextLabel: optional banner line override (e.g. a milestone reason for
 *     M2's A2.6); falls back to the creator's alert_status when omitted.
 *   - contextTone: optional 'red'|'amber'|'cyan'|'green' for the banner accent.
 *   - sourceActivity: M6 source tag for the action_log entry (default
 *     'creator_alert'); M2 passes 'new_creator_at_risk'.
 */
const TONE = { red: TCN.red, amber: TCN.amber, cyan: TCN.cyan, green: TCN.green };

const ActionPlanModal = ({
  open,
  onClose,
  onSubmit,
  creator,
  contextLabel,
  contextTone,
  sourceActivity = 'creator_alert',
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!title.trim() || !description.trim()) {
      setError(t('tcn.m4.actionPlan.required', 'Title and description are required'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit?.({
        title: title.trim(),
        description: description.trim(),
        due_date: dueDate,
        source_activity: sourceActivity,
        source_entity_type: 'creator',
        source_entity_id: creator?.creator_id ?? null,
      });
      setTitle('');
      setDescription('');
      onClose?.();
    } catch (err) {
      setError(err?.message || t('tcn.m4.actionPlan.error', 'Failed to save'));
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 520,
          background: TCN.surface1,
          border: `1px solid ${TCN.borderStrong}`,
          borderRadius: '20px 20px 0 0',
          padding: 20,
          color: TCN.text,
          fontFamily: TCN.fontSans,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <h2
            style={{
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              margin: 0,
            }}
          >
            {t('tcn.m4.actionPlan.title', 'Create action plan')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              color: TCN.textDim,
              cursor: 'pointer',
              padding: 4,
            }}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        {/* A4.7 / A2.6 — creator context banner (mockup) */}
        {creator && (() => {
          const alert = creator.alert_status || 'none';
          const accent = contextLabel
            ? TONE[contextTone] || TCN.amber
            : alert === 'critical'
              ? TCN.red
              : alert === 'at_risk'
                ? TCN.amber
                : TCN.cyan;
          const handle = creator.tiktok_username || creator.display_name || 'creator';
          const line = contextLabel
            ? contextLabel
            : alert === 'none'
              ? t('tcn.m4.actionPlan.contextOk', 'On track — document a growth action')
              : t(`tcn.m4.alert.${alert}`, alert.replace('_', ' '));
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: 14,
                marginBottom: 16,
                borderRadius: 14,
                background: `linear-gradient(135deg, ${accent}1c, transparent)`,
                border: `1px solid ${accent}55`,
              }}
            >
              <Avatar name={creator.display_name || creator.tiktok_username || ''} size={44} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: TCN.text }}>@{handle}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: accent, marginTop: 2 }}>
                  {line}
                </div>
              </div>
            </div>
          );
        })()}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field
            label={t('tcn.m4.actionPlan.titleField', 'Title')}
            value={title}
            onChange={setTitle}
            placeholder={t('tcn.m4.actionPlan.titlePh', 'Push to rank-up T6')}
          />
          <Field
            label={t('tcn.m4.actionPlan.description', 'Description')}
            value={description}
            onChange={setDescription}
            placeholder={t(
              'tcn.m4.actionPlan.descPh',
              'Coach to add 2h LIVE/day this week'
            )}
            multiline
          />
          <Field
            label={t('tcn.m4.actionPlan.dueDate', 'Due date')}
            value={dueDate}
            onChange={setDueDate}
            type="date"
          />
        </div>

        {error && (
          <div
            style={{
              marginTop: 12,
              padding: 10,
              borderRadius: 8,
              background: `${TCN.red}1f`,
              border: `1px solid ${TCN.red}33`,
              color: TCN.red,
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ marginTop: 18, display: 'flex', gap: 10 }}>
          <CTAButton variant="secondary" onClick={onClose} type="button">
            {t('tcn.m4.actionPlan.cancel', 'Cancel')}
          </CTAButton>
          <CTAButton variant="primary" type="submit" loading={submitting}>
            {t('tcn.m4.actionPlan.save', 'Save')}
          </CTAButton>
        </div>
      </form>
    </div>,
    document.body
  );
};

const Field = ({ label, value, onChange, placeholder, type = 'text', multiline = false }) => {
  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    background: TCN.surface2,
    border: `1px solid ${TCN.border}`,
    borderRadius: 10,
    color: TCN.text,
    fontFamily: 'inherit',
    // FRM-001/TCH-001 — 16px : pas de zoom iOS au focus (app manager mobile).
    fontSize: 16,
    outline: 'none',
  };
  return (
    <div>
      <label
        style={{
          display: 'block',
          fontSize: 11,
          fontWeight: 700,
          color: TCN.textDim,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={inputStyle}
        />
      )}
    </div>
  );
};

export default ActionPlanModal;
